import React, { useEffect, useMemo, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Box,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import type { LocationOption } from "@/types/locations";
import LocationService from "@/services/locations";
import SiteDocumentationService from "@/services/siteDocumentation";
import { TimelineService } from "@/services/TimelineService";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { selectSelectedSiteObject } from "@/store/selectors";
import { fetchLocationsByGdmNumber } from "@/store/slices/locationSlice";
import { useAuth0 } from "@auth0/auth0-react";
import "@/styles/modal.scss";
import { DATE_FORMAT_DISPLAY } from "@/constants";
import { formatDate, formatToApiDate } from "@/utils/date";
import { formatErrorMessage } from "@/utils/helpers";
import HierarchicalLocationSelect from "@/components/common/HierarchicalLocationSelect";

interface AddSiteDocumentationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingDocumentation?: any;
}

const AddSiteDocumentationModal: React.FC<AddSiteDocumentationModalProps> = ({
  open,
  onClose,
  onSuccess,
  existingDocumentation,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();

  // Redux selectors
  const selectedSiteObject = useAppSelector(selectSelectedSiteObject);
  const { data: userData } = useAppSelector((state) => state.user);
  const locationsData = useAppSelector((state) => state.location.data);
  const locationsLoading = useAppSelector((state) => state.location.loading);

  const isEditMode = useMemo(
    () => Boolean(existingDocumentation?.id),
    [existingDocumentation]
  );
  const [docName, setDocName] = useState(existingDocumentation?.name || "");
  const [docType, setDocType] = useState(
    existingDocumentation?.type_id || existingDocumentation?.type || ""
  );
  const [location, setLocation] = useState(
    existingDocumentation?.original_data?.location_id ||
      existingDocumentation?.location ||
      "",
  );
  const [reportDate, setReportDate] = useState(
    existingDocumentation?.report_date || ""
  );
  const [uploadedDate, setUploadedDate] = useState(
    existingDocumentation?.uploaded_date || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);

  // Fetch locations from Redux when site changes
  useEffect(() => {
    const loadLocations = async () => {
      if (!selectedSiteObject?.gdm_customer_number || !userData?.id) return;

      console.log("AddSiteDocModal: Loading locations for site", {
        gdmNumber: selectedSiteObject.gdm_customer_number,
      });

      try {
        const accessToken = await getAccessTokenSilently();
        await dispatch(
          fetchLocationsByGdmNumber({
            gdmNumber: selectedSiteObject.gdm_customer_number,
            userGuid: userData.id,
            accessToken,
          })
        ).unwrap();
        console.log("AddSiteDocModal: Locations loaded successfully");
      } catch (error) {
        console.error("AddSiteDocModal: Failed to fetch locations:", error);
      }
    };

    loadLocations();
  }, [selectedSiteObject?.gdm_customer_number, userData?.id, dispatch, getAccessTokenSilently]);

  const fetchDocTypes = async () => {
    setDocTypesLoading(true);
    try {
      const siteDocService = new SiteDocumentationService();
      const res = await siteDocService.getDocumentTypes();
      console.log("Document Types API response:", res);
      const types = res?.results || [];
      console.log("Document Types parsed list:", types);
      setDocTypes(types);
    } catch (err) {
      console.error("Failed to fetch document types:", err);
      setDocTypes([]);
    } finally {
      setDocTypesLoading(false);
    }
  };

  useEffect(() => {
    fetchDocTypes();
  }, []);

  // Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{ name: string; uploadedAt: Date; blobName?: string }>
  >([]);
  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Only allow PDF, Word, Excel
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!allowedTypes.includes(file.type)) {
        setError(
          t(
            "unsupportedFileType",
            "Unsupported file type. Please upload a PDF, WORD, or EXCEL file.",
          ),
        );
        event.target.value = "";
        return;
      }
      // File size check (20MB = 20 * 1024 * 1024 bytes)
      if (file.size > 20 * 1024 * 1024) {
        setError(
          t(
            "fileSizeExceedsLimit",
            "File size exceeds the maximum limit. Please upload a file smaller than 20MB.",
          ),
        );
        event.target.value = "";
        return;
      }
      setSelectedFile(file);
      setUploadProgress(0);
      setError("");
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const accessToken = await getAccessTokenSilently();
      const timelineService = new TimelineService(accessToken);

      setUploadProgress(25);
      const { uploadUrl, blobName } = await timelineService.generateUploadUrl(
        selectedFile.name
      );
      setUploadProgress(50);
      await timelineService.uploadFile(uploadUrl, selectedFile);
      setUploadProgress(100);
      setUploadedFiles([
        // ...uploadedFiles,
        {
          name: selectedFile.name,
          uploadedAt: new Date(),
          blobName: blobName,
        },
      ]);
      setSelectedFile(null);
      setUploadProgress(0);
      setIsUploading(false);
    } catch (error) {
      console.error("File upload failed:", error);
      setError(
        `${t("fileUploadFailed", "File upload failed:")} ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveUploadedFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    if (updatedFiles.length === 0 && !selectedFile) {
      setError(t("fileUploadRequired", "A file upload is required."));
    }
  };

  const handleDownloadFile = async (file: {
    name: string;
    blobName?: string;
  }) => {
    if (!file.blobName) {
      console.error("No blob name available for download");
      setError(t("unableToDownloadFileMissingBlob", "Unable to download file - missing blob information"));
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();
      const timelineService = new TimelineService(accessToken);

      const { downloadUrl } = await timelineService.generateDownloadUrl(
        "documents", // container name - adjust if needed
        file.name,
        file.blobName
      );

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      setError(
        `${t("downloadFailed", "Download failed:")} ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  // Reset form and expanded locations when modal opens/closes or when existingDocumentation changes
  useEffect(() => {
    console.log("Modal effect triggered:", {
      open,
      existingDocumentation,
      isEditMode,
    });

    if (!open) return;

    if (existingDocumentation?.id) {
      console.log("Initializing edit mode with data:", existingDocumentation);
      setDocName(existingDocumentation.name || "");

      // Only set docType if docTypes are already loaded, otherwise let the docTypes useEffect handle it
      if (docTypes.length > 0) {
        const existingTypeId =
          existingDocumentation.type_id || existingDocumentation.type || "";

        // Try to find matching type by ID first, then by name
        let matchingType = docTypes.find(
          (type) => String(type.id) === String(existingTypeId),
        );

        if (!matchingType) {
          // If no match by ID, try to match by name
          matchingType = docTypes.find(
            (type) =>
              type.name &&
              String(type.name).toLowerCase() ===
                String(existingTypeId).toLowerCase(),
          );
        }

        setDocType(matchingType ? String(matchingType.id) : "");
      }

      // Handle location - check multiple possible locations for the ID
      let existingLocationId = "";

      // Try to get location_id from various possible sources
      if (existingDocumentation.original_data?.location_id) {
        existingLocationId = String(
          existingDocumentation.original_data.location_id,
        );
      } else if (existingDocumentation.location_id) {
        existingLocationId = String(existingDocumentation.location_id);
      } else if (existingDocumentation.location) {
        // Check if location is an object with id property
        if (
          typeof existingDocumentation.location === "object" &&
          existingDocumentation.location !== null
        ) {
          existingLocationId = String(existingDocumentation.location.id || "");
        } else {
          // It might be a direct ID value
          existingLocationId = String(existingDocumentation.location);
        }
      }

      if (existingLocationId && isValidLocationId(existingLocationId)) {
        setLocation(existingLocationId);
      } else {
        setLocation("");
      }
      setReportDate(existingDocumentation.report_date || "");
      // Set existing uploaded file if available
      if (existingDocumentation.file_name) {
        setUploadedFiles([
          {
            name: existingDocumentation.file_name,
            uploadedAt: new Date(
              existingDocumentation.upload_date ||
                existingDocumentation.uploaded_date ||
                Date.now(),
            ),
            blobName:
              existingDocumentation.file_path ||
              existingDocumentation.file_name,
          },
        ]);
      } else {
        setUploadedFiles([]);
      }
    } else {
      console.log("Initializing create mode");
      setDocName("");
      setDocType("");
      setLocation("");
      setReportDate("");
      setUploadedFiles([]);
    }

    setSelectedFile(null);
    setError("");
  }, [open, existingDocumentation, isEditMode, docTypes]);

  const isValidLocationId = (value: string | number): boolean => {
    if (!value || value === "") return true; // Empty is valid (optional field)
    const numValue = Number(value);
    return !isNaN(numValue) && isFinite(numValue) && numValue > 0;
  };

  const validateForm = () => {
    return docName && docType && reportDate && uploadedFiles.length > 0;
  };

  const performSubmit = async () => {
    if (!selectedSiteObject?.site_id || !userData?.id) return;
    setLoading(true);
    setError("");

    try {
      // Prepare form data for submission
      const formData = new FormData();
      formData.append("site_id", String(selectedSiteObject.site_id));
      const userGuid = userData.id;
      formData.append("user_id", userGuid);
      formData.append("modified_by", userGuid);

      formData.append("name", docName);
      formData.append(
        "report_date",
        reportDate ? dayjs(reportDate).format("YYYY-MM-DDTHH:mm:ss") : ""
      );
      formData.append(
        "customer_sites_report_type_id",
        docType ? String(docType) : ""
      );

      // Handle file information - use existing file if no new file uploaded
      if (uploadedFiles.length > 0) {
        const uploadedFile = uploadedFiles[0];
        formData.append("file_path", uploadedFile.blobName || "");
      }

      // Only append location_id if it's a valid numeric value
      if (location && isValidLocationId(location)) {
        formData.append("location_id", String(location));
      } else if (isEditMode && !location) {
        // Send 0 as sentinel value to signal backend to set location_id to NULL in the database
        formData.append("location_id", "0");
      } else if (location && !isValidLocationId(location)) {
        console.warn("Invalid location_id value detected, skipping:", location);
      }

      console.log("Submitting site documentation:", formData);
      console.log(
        "Edit mode:",
        isEditMode,
        "Document ID:",
        existingDocumentation?.id
      );

      const siteDocService = new SiteDocumentationService();
      let response;

      if (isEditMode && existingDocumentation?.id) {
        response = await siteDocService.updateDocumentation(
          existingDocumentation.id,
          formData
        );
        console.log("Site documentation updated successfully:", response);
      } else {
        response = await siteDocService.createDocumentation(formData);
        console.log("Site documentation created successfully:", response);
      }

      // Check for error in successful response (200 status with error message)
      if (response?.error_message) {
        setError(response.error_message);
        setLoading(false);
        return;
      }

      onSuccess();
      setLoading(false);
    } catch (error) {
      const errorMessage = formatErrorMessage(
        error,
        isEditMode
          ? "Failed to update site documentation"
          : "Failed to create site documentation"
      );
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError(
        t(
          "pleaseFillAllMandatoryFields",
          "Please fill all mandatory fields: DOCUMENT NAME, DOCUMENT TYPE, DOCUMENT DATE",
        ),
      );
      return;
    }

    // A file is required for both new and edited documents
    if (uploadedFiles.length === 0 && !selectedFile) {
      setError(t("fileUploadRequired", "A file upload is required."));
      return;
    }

    // Proceed with submission
    performSubmit();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            className: "modal-dialog",
          },
        }}
      >
        <Box className="modal-header">
          <DialogTitle className="modal-header-title">
            {isEditMode
              ? t("editDocumentation", "EDIT DOCUMENT")
              : t("createDocumentation", "CREATE DOCUMENT")}
          </DialogTitle>
          <IconButton
            onClick={onClose}
            size="small"
            className="modal-close-button"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent
          className="modal-content-padded"
          sx={{ pb: "8px !important" }}
        >
          <Typography
            variant="h6"
            className="modal-section-title"
            sx={{ pt: 0, mt: 0, fontSize: "12px", fontWeight: "bold" }}
          >
            {isEditMode
              ? t("editDocumentInfo", "UPLOAD DOCUMENT")
              : t("uploadDocument", "UPLOAD DOCUMENT")}
          </Typography>
          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                mb: 2,
                borderRadius: 0,
                fontWeight: 500,
                fontSize: "1rem",
                background: "#f8f8f8",
                color: "#333",
                border: "none",
                boxShadow: "none",
              }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setError("")}
                  sx={{ mt: "-4px" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          <Box
            sx={{
              backgroundColor: "#F3F3F3",
              px: 3,
              mb: 2,
              paddingTop: 4,
              paddingBottom: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              textAlign="center"
              sx={{ fontSize: "16px" }}
            >
              {t("uploadDocumentFile", "Upload Document File")}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
              sx={{ mb: 1, mt: 1, fontSize: "14px" }}
            >
              {t("supportedFileTypes", "Supported file types:")}{" "}
              <b>{t("acceptedFileTypes", "PDF, Word, Excel")}</b>
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
              sx={{ mb: 3, fontSize: "14px" }}
            >
              {t("maximumFileSize", "Maximum file size:")}{" "}
              <b>{t("twentyMB", "20 MB")}</b>
            </Typography>
            {/* File Selection Area */}
            {!selectedFile && !isUploading && (
              <Box textAlign="center" mb={3}>
                <Button
                  variant="outlined"
                  component="label"
                  // size="large"
                  className="text-uppercase"
                  sx={{
                    borderRadius: "0px",
                    borderColor: "#262728",
                    color: "#262728",
                    width: "300px",
                    backgroundColor: "white",
                    padding: "12px",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    fontSize: "12px",
                    "&:hover": {
                      borderColor: "#00573d",
                      color: "#00573d",
                      backgroundColor: "white",
                    },
                  }}
                >
                  {t("browseFiles", "Browse Files")}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                </Button>
              </Box>
            )}
            {/* Selected File Display with Upload Button */}
            {selectedFile && !isUploading && (
              <Box
                sx={{ backgroundColor: "white", p: 3, borderRadius: 1, mb: 3 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t("selectedFile", "Selected File:")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        wordBreak: "break-word",
                        flex: { xs: "1 1 100%", sm: "initial" },
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleFileUpload}
                    data-testid="upload-file-button"
                    sx={{
                      backgroundColor: "#00573d",
                      color: "white",
                      textTransform: "none",
                      borderRadius: 0,
                      px: 3,
                      width: { xs: "100%", md: "auto" },
                      "&:hover": {
                        backgroundColor: "#004530",
                      },
                    }}
                  >
                    {t("uploadFile", "Upload File")}
                  </Button>
                </Box>
              </Box>
            )}
            {/* Upload Progress */}
            {isUploading && (
              <Box
                sx={{ backgroundColor: "white", p: 3, borderRadius: 1, mb: 3 }}
              >
                <Typography variant="body2" gutterBottom>
                  {t("uploadingFile", "Uploading:")} {selectedFile?.name}
                </Typography>
                <Box sx={{ width: "100%", mb: 1 }}>
                  <Box
                    sx={{
                      height: 8,
                      backgroundColor: "#e0e0e0",
                      borderRadius: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${uploadProgress}%`,
                        height: 8,
                        backgroundColor: "#00573d",
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {uploadProgress}% {t("complete", "Complete")}
                </Typography>
              </Box>
            )}
            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <Box sx={{ backgroundColor: "white", p: 3, borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {t("uploadedFiles", "Uploaded Files")}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {uploadedFiles.map((file, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 2,
                        mb: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        "&:hover": { backgroundColor: "#f9f9f9" },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t("uploadedAt", "Uploaded at:")}{" "}
                          {file.uploadedAt.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(file)}
                          data-testid={`download-file-${index}`}
                          sx={{
                            color: "#00573d",
                            "&:hover": {
                              backgroundColor: "rgba(0, 87, 61, 0.04)",
                            },
                          }}
                          title={t("downloadFile", "Download file")}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveUploadedFile(index)}
                          data-testid={`remove-file-${index}`}
                          sx={{
                            color: "#d32f2f",
                            "&:hover": {
                              backgroundColor: "rgba(211, 47, 47, 0.04)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <form className="form">
            <Grid container spacing={2} sx={{ mb: error ? 2 : 4, mx: "auto" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label
                  className="form-label"
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {t("documentNameLabel", "DOCUMENT NAME (MANDATORY)")}
                </label>
                <TextField
                  label=""
                  variant="outlined"
                  value={docName}
                  className="input select"
                  onChange={(e) => setDocName(e.target.value)}
                  size="medium"
                  fullWidth
                  disabled={loading || locationsLoading}
                  data-testid="document-name-input"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label
                  className="form-label"
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {t("documentTypeLabel", "DOCUMENT TYPE (MANDATORY)")}
                </label>
                <Select
                  value={docType || ""}
                  onChange={(e) => setDocType(e.target.value)}
                  fullWidth
                  variant="outlined"
                  className="input select"
                  disabled={loading || docTypesLoading}
                  displayEmpty
                  data-testid="document-type-select"
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9ca3af" }}>
                          {t("select", "Select")}
                        </span>
                      );
                    }
                    const selectedType = docTypes.find(
                      (type) => String(type.id) === String(selected),
                    );
                    return selectedType ? selectedType.name : "";
                  }}
                >
                  {docTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label
                  className="form-label"
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {t("documentDateLabel", "DOCUMENT DATE (MANDATORY)")}
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={reportDate ? dayjs(reportDate) : null}
                    onChange={(date) =>
                      setReportDate(date ? date.format("MM/DD/YYYY") : "")
                    }
                    format="MM/DD/YYYY"
                    className="input select"
                    enableAccessibleFieldDOMStructure={false}
                    slots={{
                      textField: (params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          disabled={loading}
                          data-testid="document-date-input"
                        />
                      ),
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <label
                  className="form-label"
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {t("locationLabel", "LOCATION (OPTIONAL)")}
                </label>
                <HierarchicalLocationSelect
                  locationsData={locationsData as LocationOption[] | undefined}
                  value={location}
                  onChange={(locationId) => {
                    setLocation(locationId);
                    console.log(
                      "AddSiteDocModal: Location changed",
                      locationId,
                    );
                  }}
                  loading={locationsLoading}
                  disabled={loading || !selectedSiteObject?.site_id}
                  placeholder={t("select", "Select")}
                  className="input select"
                  disableClearable={false}
                  fullWidth={true}
                  autoExpandToSelected={true}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions
          className="modal-actions-flex"
          sx={{ pb: "8px !important" }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={loading}
            className="modal-button-base modal-button-outlined"
            sx={{
              fontSize: "12px",
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
          >
            {t("cancelButton", "CANCEL")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            className="modal-button-base modal-button-primary"
            data-testid="submit-button"
            sx={{
              fontSize: "12px",
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
            disabled={!validateForm() || loading || locationsLoading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              t("updateDocumentation", "UPDATE DOCUMENTATION")
            ) : (
              t("addDocumentation", "ADD DOCUMENTATION")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddSiteDocumentationModal;
